import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { IconComponent } from '../../../shared/components/icon/icon.component';

export interface ExplainerScene {
  src: string;
  alt: string;
  kicker: string;
  title: string;
  body: string;
}

const SCENE_MS = 6500;

@Component({
  selector: 'app-secure-trade-explainer',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './secure-trade-explainer.component.html',
  styleUrl: './secure-trade-explainer.component.css',
})
export class SecureTradeExplainerComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly player = viewChild<ElementRef<HTMLElement>>('player');

  readonly scenes: ExplainerScene[] = [
    {
      src: 'assets/marketing/secure-trade-hero.webp',
      alt: 'A leather saddle on a fence beside a long Australian country road',
      kicker: 'The gap',
      title: 'The right saddle. The wrong postcode.',
      body: 'Marketplace deals stop at the suburb. The one you actually want is often a state away.',
    },
    {
      src: 'assets/marketing/secure-trade-agree.webp',
      alt: 'A rider outdoors on her phone, agreeing a remote saddle deal',
      kicker: 'Bring it on-platform',
      title: 'Agree the deal. Invite the other person.',
      body: 'Either of you starts a Secure Trade. They get an email — no leap, no cash in an envelope.',
    },
    {
      src: 'assets/marketing/secure-trade-held.webp',
      alt: 'A leather saddle beside a brass lock and key',
      kicker: 'Payment held',
      title: 'The money sits with Galopa. Not with a stranger.',
      body: 'The buyer pays in. Funds are locked until the saddle is in hand and confirmed as described.',
    },
    {
      src: 'assets/marketing/secure-trade-highway.webp',
      alt: 'A courier van on a long rural Australian highway at golden hour',
      kicker: 'Australia-wide',
      title: 'They ship. Perth to Hobart. Darwin to Sydney.',
      body: 'Knowing the payment is real, the seller posts it — tracked, not left on a porch in hope.',
    },
    {
      src: 'assets/marketing/secure-trade-inspect.webp',
      alt: 'A rider inspecting a leather saddle in a sunlit tack room',
      kicker: 'You hold the key',
      title: 'Inspect it. Then — and only then — funds release.',
      body: 'Not as described? Flag it. Nothing moves until you say so.',
    },
  ];

  readonly index = signal(0);
  readonly playing = signal(false);
  readonly progress = signal(0);

  private raf = 0;
  private lastTs = 0;
  private elapsed = 0;
  private inView = false;
  private reducedMotion = false;
  private userPaused = false;
  private observer?: IntersectionObserver;

  constructor() {
    afterNextRender(() => this.setup());
    this.destroyRef.onDestroy(() => this.teardown());
  }

  togglePlayback(): void {
    if (this.reducedMotion) return;
    const next = !this.playing();
    this.playing.set(next);
    this.userPaused = !next;
    this.lastTs = 0;
  }

  goTo(i: number): void {
    this.index.set(i);
    this.elapsed = 0;
    this.progress.set(0);
    this.lastTs = 0;
  }

  segmentWidth(i: number): number {
    const current = this.index();
    if (i < current) return 100;
    if (i === current) return this.progress() * 100;
    return 0;
  }

  private setup(): void {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const el = this.player()?.nativeElement;
    if (!el) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        this.inView = entries.some((e) => e.isIntersecting);
        if (this.inView && !this.reducedMotion && !this.userPaused) {
          this.playing.set(true);
          this.lastTs = 0;
        }
        if (!this.inView) {
          this.playing.set(false);
        }
      },
      { threshold: 0.35 },
    );
    this.observer.observe(el);

    const onVis = () => {
      if (document.hidden) this.playing.set(false);
    };
    document.addEventListener('visibilitychange', onVis);
    this.destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', onVis));

    this.raf = requestAnimationFrame(this.tick);
  }

  private teardown(): void {
    cancelAnimationFrame(this.raf);
    this.observer?.disconnect();
  }

  private readonly tick = (ts: number) => {
    this.raf = requestAnimationFrame(this.tick);
    if (!this.playing() || this.reducedMotion || !this.inView) {
      this.lastTs = 0;
      return;
    }
    if (!this.lastTs) {
      this.lastTs = ts;
      return;
    }
    this.elapsed += ts - this.lastTs;
    this.lastTs = ts;
    this.progress.set(Math.min(1, this.elapsed / SCENE_MS));
    if (this.elapsed >= SCENE_MS) {
      this.elapsed = 0;
      this.progress.set(0);
      this.index.update((i) => (i + 1) % this.scenes.length);
    }
  };
}
